import React, { useMemo } from "react";

const RenderSharedStatus = ({ siteStatusData, numLoading = false }) => {
  const normalizedData = useMemo(() => {
    const normalized = {};
    Object.entries(siteStatusData).forEach(([site, dateData]) => {
      normalized[site] = {};
      Object.entries(dateData).forEach(([date, status]) => {
        if (typeof status === "object" && status !== null) {
          normalized[site][date] = {
            exists: status.exists?.toLowerCase?.() || "n/a",
            totalAssignWorkWithImages: status.totalAssignWorkWithImages ?? 0,
            totalAssignWorkOne: status.totalAssignWorkOne ?? 0,
          };
        } else {
          normalized[site][date] = {
            exists: typeof status === "string" ? status.toLowerCase() : "n/a",
            totalAssignWorkWithImages: 0,
            totalAssignWorkOne: 0,
          };
        }
      });
    });
    return normalized;
  }, [siteStatusData]);

  const dates = useMemo(() => {
    const allDates = new Set();
    Object.values(normalizedData).forEach((siteData) => {
      Object.keys(siteData).forEach((date) => {
        allDates.add(date);
      });
    });

    return Array.from(allDates).sort((a, b) => {
      const [dayA, monthA, yearA] = a.split("/").map(Number);
      const [dayB, monthB, yearB] = b.split("/").map(Number);
      if (yearA !== yearB) return yearA - yearB;
      if (monthA !== monthB) return monthA - monthB;
      return dayA - dayB;
    });
  }, [normalizedData]);

  const getStatusColor = (status) => {
    // Enhanced professional color palette
    const baseColors = {
      "n/a": "#f0f2f5",
      yes: "#1e8e3e", // Deeper professional green
      no: "#d93025",  // More sophisticated red
      default: "#f8f9fa",
    };

    const opacity = numLoading ? 0.5 : 1;

    if (!status || status === "n/a")
      return numLoading ? "rgba(240, 242, 245, 0.5)" : baseColors["n/a"];
    if (status === "yes")
      return numLoading ? "rgba(30, 142, 62, 0.5)" : baseColors["yes"];
    if (status === "no")
      return numLoading ? "rgba(217, 48, 37, 0.5)" : baseColors["no"];

    return numLoading ? "rgba(248, 249, 250, 0.5)" : baseColors["default"];
  };

  const getOverlayColor = (percentage, total) => {
    // More nuanced color palette for percentage badges
    if (total === 0) return "#757575"; // Gray for no assignments
    if (percentage >= 1) return "#0d652d"; // Deep green for 100% completion
    if (percentage >= 0.75) return "#1e8e3e"; // Professional green for high completion
    if (percentage >= 0.5) return "#188038"; // Medium green
    if (percentage >= 0.25) return "#e37400"; // Amber for lower completion
    return "#c5221f"; // Professional red for very low completion
  };

  const getStatusIcon = (status) => {
    if (!status || status === "n/a") return "—";
    if (status === "yes") return "✓";
    if (status === "no") return "✗";
    return status;
  };

  const statusCounts = useMemo(() => {
    const counts = {};
    dates.forEach((date) => {
      let yesCount = 0;
      let noCount = 0;
      Object.values(normalizedData).forEach((siteData) => {
        if (siteData[date]?.exists === "yes") yesCount++;
        if (siteData[date]?.exists === "no") noCount++;
      });
      counts[date] = { yes: yesCount, no: noCount };
    });
    return counts;
  }, [normalizedData, dates]);

  const styles = {
    container: {
      overflowX: "auto",
      overflowY: "auto",
      maxHeight: "100vh",
      fontFamily:
        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      borderRadius: "12px",
      boxShadow: "0 2px 20px rgba(0, 0, 0, 0.08)",
      border: "1px solid #eaecef",
      opacity: numLoading ? 0.8 : 1,
      background: "#ffffff",
    },
    table: {
      width: "100%",
      backgroundColor: "white",
      borderCollapse: "separate",
      borderSpacing: 0,
      borderRadius: "12px",
    },
    headerRow: {
      backgroundColor: numLoading ? "rgba(249, 250, 251, 0.5)" : "#f9fafb",
      position: "sticky",
      top: 0,
      zIndex: 10,
    },
    headerCell: {
      padding: "18px 16px",
      borderBottom: "1px solid #eaecef",
      fontWeight: 600,
      color: numLoading ? "rgba(55, 65, 81, 0.5)" : "#374151",
      textAlign: "center",
      fontSize: "14px",
      whiteSpace: "nowrap",
      transition: "background-color 0.2s ease",
    },
    siteNameCell: {
      padding: "16px",
      borderBottom: "1px solid #eaecef",
      fontWeight: 600,
      textAlign: "left",
      position: "sticky",
      left: 0,
      backgroundColor: numLoading ? "rgba(255, 255, 255, 0.5)" : "white",
      zIndex: 5,
      boxShadow: "2px 0 8px rgba(0, 0, 0, 0.03)",
      color: "#202124",
    },
    dataCell: {
      padding: "14px",
      borderBottom: "1px solid #eaecef",
      textAlign: "center",
      fontWeight: 500,
      transition: "background-color 0.2s ease",
    },
    evenRow: {
      backgroundColor: numLoading ? "rgba(255, 255, 255, 0.5)" : "white",
    },
    oddRow: {
      backgroundColor: numLoading ? "rgba(249, 250, 251, 0.5)" : "#f9fafb",
    },
    countBadge: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 10px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: 600,
      marginLeft: "6px",
      opacity: numLoading ? 0.5 : 1,
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
    },
    yesBadge: {
      backgroundColor: numLoading ? "rgba(232, 245, 233, 0.5)" : "#e8f5e9",
      color: numLoading ? "rgba(13, 101, 45, 0.5)" : "#0d652d",
    },
    noBadge: {
      backgroundColor: numLoading ? "rgba(253, 236, 234, 0.5)" : "#fdecec",
      color: numLoading ? "rgba(197, 34, 31, 0.5)" : "#c5221f",
    },
    statusIcon: {
      fontWeight: "bold",
      opacity: numLoading ? 0.6 : 1,
      fontSize: "16px",
    },
    legend: {
      display: "flex",
      padding: "16px",
      backgroundColor: numLoading ? "rgba(249, 250, 251, 0.5)" : "#f9fafb",
      borderRadius: "8px",
      margin: "12px 16px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
    },
    legendItem: {
      display: "flex",
      alignItems: "center",
      marginRight: "20px",
      fontSize: "14px",
      opacity: numLoading ? 0.7 : 1,
    },
    legendColor: {
      width: "16px",
      height: "16px",
      marginRight: "8px",
      borderRadius: "4px",
      border: "1px solid rgba(0,0,0,0.05)",
    },
    tableHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "20px 16px",
      borderBottom: "1px solid #eaecef",
      backgroundColor: "#fff",
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
    },
    tableTitle: {
      fontSize: "18px",
      fontWeight: 600,
      color: "#202124",
      letterSpacing: "-0.01em",
    },
    tableWrapper: {
      padding: "8px",
    },
    statusCell: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "36px",
      width: "36px",
      margin: "0 auto",
      borderRadius: "50%",
      fontSize: "14px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      color: "#fff", // White text for better contrast
    },
    overlay: {
      position: "absolute",
      bottom: -6,
      right: -6,
      fontSize: "10px",
      padding: "3px 6px",
      borderRadius: "999px",
      fontWeight: 600,
      color: "white",
      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      zIndex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "28px",
      letterSpacing: "-0.02em",
    },
    progressLegend: {
      display: "flex",
      padding: "12px 16px",
      backgroundColor: numLoading ? "rgba(249, 250, 251, 0.5)" : "#f9fafb",
      borderRadius: "8px",
      margin: "12px 16px 0 16px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    progressTitle: {
      fontSize: "14px",
      fontWeight: 600,
      marginRight: "20px",
      color: "#202124",
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.tableHeader}>
        <div style={styles.tableTitle}>NMR Share Status Dashboard</div>
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <div
              style={{
                ...styles.legendColor,
                backgroundColor: getStatusColor("yes"),
              }}
            ></div>
            <span>Yes (✓)</span>
          </div>
          <div style={styles.legendItem}>
            <div
              style={{
                ...styles.legendColor,
                backgroundColor: getStatusColor("no"),
              }}
            ></div>
            <span>No (✗)</span>
          </div>
          <div style={styles.legendItem}>
            <div
              style={{
                ...styles.legendColor,
                backgroundColor: getStatusColor("n/a"),
              }}
            ></div>
            <span>N/A (—)</span>
          </div>
        </div>
      </div>
      
      <div style={styles.progressLegend}>
        <div style={styles.progressTitle}>Badge Shows: Team with images / Total Teams</div>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th
                style={{
                  ...styles.headerCell,
                  textAlign: "left",
                  borderRight: "1px solid #eaecef",
                }}
              >
                Site
              </th>
              {dates.map((date) => (
                <th key={date} style={styles.headerCell}>
                  <div>{date}</div>
                  <div style={{ marginTop: "6px" }}>
                    <span style={{ ...styles.countBadge, ...styles.yesBadge }}>
                      {statusCounts[date].yes}
                    </span>
                    <span style={{ ...styles.countBadge, ...styles.noBadge }}>
                      {statusCounts[date].no}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(normalizedData).map(
              ([siteName, dateStatuses], index) => (
                <tr
                  key={siteName}
                  style={index % 2 === 0 ? styles.evenRow : styles.oddRow}
                >
                  <td
                    style={{
                      ...styles.siteNameCell,
                      ...(index % 2 !== 0
                        ? { backgroundColor: "#f9fafb" }
                        : {}),
                    }}
                  >
                    {siteName}
                  </td>
                  {dates.map((date) => {
                    const statusItem = dateStatuses[date] || {};
                    const status = statusItem.exists || "n/a";
                    const completed = statusItem.totalAssignWorkWithImages || 0;
                    const total = statusItem.totalAssignWorkOne || 0;
                    const percentage = total > 0 ? completed / total : 0;

                    return (
                      <td key={`${siteName}-${date}`} style={styles.dataCell}>
                        <div
                          style={{
                            ...styles.statusCell,
                            backgroundColor: getStatusColor(status),
                          }}
                        >
                          <span style={styles.statusIcon}>
                            {getStatusIcon(status)}
                          </span>
                          <div
                            style={{
                              ...styles.overlay,
                              backgroundColor: getOverlayColor(percentage, total),
                            }}
                            title={total === 0 ? "No assignments for this day" : `${completed}/${total} assignments completed`}
                          >
                            {`${completed}/${total}`}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RenderSharedStatus;